import { useTranslation } from 'react-i18next';
import { SyntheticEvent, useMemo } from 'react';
import { Select, RadioList, DateTools, DatePicker, Input, TFileWithType } from '@treezor/design-system';

import { useCreateSelfEmployedUser } from '@/features/user/hooks/createUser/useCreateSelfEmployedUser';
import { SanctionEmbargoQuestionaire } from '@/features/user/components/CreateUser/SanctionEmbargoQuestionaire';
import { ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';
import { USResident } from '@/features/user/components/CreateUser/USResident';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import {
  ContentLayout,
  CustomColumns,
  Section,
  TwoColumns,
} from '@/features/user/components/CreateUser/layout/ContentLayout';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';
import { CreateUserTaxResidences } from '@/features/user/components/CreateUser/CreateUserTaxResidences';
import { CreateUserKycDocuments } from '@/features/user/components/CreateUser/CreateUserKycDocuments';

import { minimumDate } from '@/hooks/useDashboardConstants';

export interface ICreateUserSelfVerificationDetailsProps {
  stepsLength: number;
}

export function CreateUserSelfVerificationDetails({ stepsLength }: ICreateUserSelfVerificationDetailsProps) {
  const { t } = useTranslation();

  const {
    countryCodeOptions,
    personalAssetOptions,
    incomeRangeOptions,
    occupationTypeOptions,
    personalAssetRanges,
    monthlyIncomeRangeOptions,
    occupationCategoryOptions,
    legalAnnualTurnOverOptions,
    legalNetIncomeRangeOptions,
    legalNumberOfEmployeeRangeOptions,
  } = useFieldOptions();

  const {
    config,
    handleSubmit,
    watches,
    user,
    handleValueChange,
    language,
    handleDateChange,
    handleInputChange,
    register,
    shouldBeUSResident,
  } = useCreateSelfEmployedUser({
    birthday: undefined,
    birthCountry: undefined,
    placeOfBirth: '',
    nationality: undefined,
    nationalityOther: undefined,
    legalAnnualTurnOver: undefined,
    legalNetIncomeRange: undefined,
    isOnStockExchange: undefined,
    legalNumberOfEmployeeRange: undefined,
    specifiedUSPerson: 0,
    taxResidences: undefined,
    kycDocuments: undefined,
    entitySanctionsQuestionnaire: undefined,
    activityOutsideEu: 0,
    economicSanctions: 0,
    residentCountriesSanctions: 0,
    involvedSanctions: 0,
    personalAssets: undefined,
    incomeRange: undefined,
    occupationType: undefined,
    occupationCategory: undefined,
    personalAssetsRange: undefined,
    monthlyIncomeRange: undefined,
  });

  const { isTaxResidenceDisabled } = useAddTaxResidences();

  const { isKycDocumentsInvalid } = useCreateUserKycDocuments();

  const birthdayWatch = watches['birthday'];
  const selectedBirthday = DateTools.localStringToDate(birthdayWatch, language);
  const specifiedUSPersonWatch = watches['specifiedUSPerson'];
  const taxResidencesWatch = watches['taxResidences'];
  const kycDocumentsWatch = watches['kycDocuments'];
  const entitySanctionsQuestionnaireWatch = watches['entitySanctionsQuestionnaire'];

  const isFormDisabled = useMemo(
    () => isTaxResidenceDisabled(taxResidencesWatch) || isKycDocumentsInvalid(kycDocumentsWatch),
    [isTaxResidenceDisabled, taxResidencesWatch, isKycDocumentsInvalid, kycDocumentsWatch],
  );

  return (
    <div className="flex flex-col gap-6">
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.birthNationality')}>
        <CustomColumns columns={[1, 2]}>
          <DatePicker
            selectedDate={selectedBirthday}
            label={t('pages.dashboard.createUser.form.birthday')}
            todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
            clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
            onChangeDate={(date) => handleDateChange(date, 'birthday')}
            language={language}
            minDate={minimumDate}
            error={
              DateTools.compare(new Date(), selectedBirthday) === 1
                ? t('pages.dashboard.createUser.form.rules.invalidDate')
                : undefined
            }
          />

          <Select
            label={t('pages.dashboard.createUser.form.birthCountry')}
            options={countryCodeOptions}
            withSearch
            defaultValue={user?.birthCountry}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'birthCountry')}
            showDelete
          />
        </CustomColumns>

        <TwoColumns>
          <Input
            type="text"
            label={t('pages.dashboard.createUser.form.placeOfBirth')}
            {...register('placeOfBirth')}
            onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleInputChange(event, 'placeOfBirth')
            }
            value={user?.placeOfBirth}
          />

          <Select
            label={t('pages.dashboard.createUser.form.nationality')}
            options={countryCodeOptions}
            withSearch
            defaultValue={user?.nationality}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'nationality')}
            showDelete
          />
        </TwoColumns>
        <AdditonalDataLayout
          shouldShow={!!watches['nationalityOther']}
          label={t('pages.dashboard.createUser.form.actions.addOtherNationality')}
        >
          <Select
            label={t('pages.dashboard.createUser.form.nationalityOther')}
            options={countryCodeOptions}
            withSearch
            defaultValue={user?.nationalityOther}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'nationalityOther')}
          />
        </AdditonalDataLayout>
      </ContentLayout>

      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.situation')}>
        <Section title={t('pages.dashboard.createUser.selfEmployed.sections.personalSituation')}>
          {config?.settings?.users?.dataQuality?.includes('personalSituation') ? (
            <>
              <Select
                label={t('pages.dashboard.createUser.form.occupation.label')}
                options={occupationCategoryOptions}
                defaultValue={user.occupationCategory}
                onChange={(value: number) => handleValueChange(value, 'occupationCategory')}
                withSearch
                noOptionsComponent={<CreateUserEmptyComponent />}
                tooltipInfo={{
                  content: t('pages.dashboard.createUser.form.occupation.tooltip'),
                  className: 'min-w-[240px]',
                }}
                showDelete
              />
              <TwoColumns>
                <Select
                  label={t('pages.dashboard.createUser.form.personalAssetsEstimated.label')}
                  options={personalAssetRanges}
                  defaultValue={user.personalAssetsRange}
                  onChange={(value: number) => handleValueChange(value, 'personalAssetsRange')}
                  withSearch
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  tooltipInfo={{
                    content: t('pages.dashboard.createUser.form.personalAssetsEstimated.tooltip'),
                    className: 'min-w-[240px]',
                  }}
                  showDelete
                />
                <Select
                  label={t('pages.dashboard.createUser.form.netMontlyIncome.label')}
                  options={monthlyIncomeRangeOptions}
                  defaultValue={user.monthlyIncomeRange}
                  onChange={(value: number) => handleValueChange(value, 'monthlyIncomeRange')}
                  withSearch
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  showDelete
                />
              </TwoColumns>{' '}
            </>
          ) : (
            <>
              <TwoColumns>
                <RadioList
                  label={t('pages.dashboard.createUser.form.personalAssets.title')}
                  options={personalAssetOptions}
                  value={watches['personalAssets']}
                  onChange={(value: string) => handleValueChange(value, 'personalAssets')}
                  className="pt-2 gap-3"
                />

                <RadioList
                  label={t('pages.dashboard.createUser.form.incomeRange.title')}
                  options={incomeRangeOptions}
                  value={watches['incomeRange']}
                  onChange={(value: string) => handleValueChange(value, 'incomeRange')}
                  className="pt-2 gap-3"
                />
              </TwoColumns>
              <RadioList
                label={t('pages.currentProfiles.profile.financial.occupationType')}
                options={occupationTypeOptions}
                value={watches['occupationType']}
                onChange={(value: string) => handleValueChange(value, 'occupationType')}
                className="pt-2 gap-3"
              />
            </>
          )}
        </Section>

        <Section title={t('pages.dashboard.createUser.selfEmployed.sections.professionalSituation')}>
          <TwoColumns>
            <RadioList
              label={t('pages.dashboard.createUser.form.legalAnnualTurnOver')}
              options={legalAnnualTurnOverOptions}
              value={watches['legalAnnualTurnOver']}
              onChange={(value: string) => handleValueChange(value, 'legalAnnualTurnOver')}
              className="pt-2 gap-3"
            />
            <div className="flex flex-col gap-6">
              <RadioList
                label={t('pages.dashboard.createUser.form.legalNetIncomeRanges.title')}
                options={legalNetIncomeRangeOptions}
                value={watches['legalNetIncomeRange']}
                onChange={(value: string) => handleValueChange(value, 'legalNetIncomeRange')}
                className="pt-2 gap-3"
              />
            </div>
          </TwoColumns>

          <Select
            label={t('pages.dashboard.createUser.form.legalNumberOfEmployee')}
            options={legalNumberOfEmployeeRangeOptions}
            defaultValue={user?.legalNumberOfEmployeeRange}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'legalNumberOfEmployeeRange')}
            showDelete
          />

          <SanctionEmbargoQuestionaire
            defaultValue={{
              entitySanction: entitySanctionsQuestionnaireWatch,
              activityOutsideEu: watches?.['activityOutsideEu']?.toString(),
              economicSanctions: watches?.['economicSanctions']?.toString(),
              residentCountriesSanctions: watches?.['residentCountriesSanctions']?.toString(),
              involvedSanctions: watches?.['involvedSanctions']?.toString(),
            }}
            handleValueChange={handleValueChange}
          />
        </Section>
      </ContentLayout>
      <ContentLayout title={t('pages.currentProfiles.profile.taxResidence.cardTitle')}>
        <USResident
          title={t('pages.dashboard.createUser.form.isUsTaxResident')}
          description={t('pages.dashboard.createUser.form.usTaxResidentDescription')}
          value={specifiedUSPersonWatch.toString()}
          handleValuesChange={(changes) => changes.map(({ value, key }) => handleValueChange(value, key))}
          taxResidences={taxResidencesWatch}
          shouldShowWarning={
            shouldBeUSResident(['country', 'secondaryCountry', 'nationality', 'birthCountry', 'nationalityOther']) &&
            specifiedUSPersonWatch !== '1'
          }
        />
        <CreateUserTaxResidences
          taxResidences={taxResidencesWatch}
          defaultCountry={user?.country}
          onUpdate={(value: ITaxResidenceCreate[]) => handleValueChange(value, 'taxResidences')}
        />
      </ContentLayout>
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.documents')} hasDivider={false}>
        <CreateUserKycDocuments
          handleFileChange={(files: TFileWithType[]) => handleValueChange(files, 'kycDocuments')}
          defaultFiles={user?.kycDocuments}
        />
      </ContentLayout>
      <CreateUserFooter
        onNext={handleSubmit}
        isNextDisabled={isFormDisabled}
        onPrev={handleSubmit}
        stepsLength={stepsLength}
      />
    </div>
  );
}
